"use client"
import {
  CollectionLockup,
  CollectionLockupContent,
  CollectionLockupTitle,
} from "@opensea/app-kit"
import type { FragmentOf } from "@opensea/graphql"
import { readFragment } from "@opensea/graphql"
import {
  Item,
  ItemAvatar,
  ItemContent,
  ItemTitle,
  QuantityInput,
  TableCell,
  TableRow,
  tableRowSizeVariants,
  UnstyledButton,
  useTable,
} from "@opensea/ui-kit"
import { Close } from "@opensea/ui-kit/icons"
import { bn } from "@opensea/utils"
import { Controller, useFieldArray, useFormContext } from "react-hook-form"
import type { TransferFlowFormValues } from "../../types"
import { TRANSFER_FLOW_FORM_ITEM_CLASSNAMES } from "./constants"
import { TransferFlowFormItemFragment } from "./TransferFlowFormItem.fragment"

type TransferFlowFormItemProps = {
  item: FragmentOf<typeof TransferFlowFormItemFragment>
  index: number
}

export const TransferFlowFormItem = ({
  item: itemKey,
  index,
}: TransferFlowFormItemProps) => {
  const { control } = useFormContext<TransferFlowFormValues>()
  const { fields, remove } = useFieldArray({
    keyName: "_id",
    control,
    name: "items",
  })

  const item = readFragment(TransferFlowFormItemFragment, itemKey)
  const { size } = useTable()
  const { image } = tableRowSizeVariants({ size })
  const showQuantityInput = item.standard === "ERC1155"
  const ownedQuantity = item.ownership?.quantity ?? "0"

  return (
    <TableRow>
      <TableCell className={TRANSFER_FLOW_FORM_ITEM_CLASSNAMES.item}>
        <Item variant="unstyled">
          <ItemAvatar className={image()} src={item.imageUrl} />
          <ItemContent>
            <ItemTitle>{item.name}</ItemTitle>
            {item.collection ? (
              <CollectionLockup collection={item.collection} size="xs">
                <CollectionLockupContent>
                  <CollectionLockupTitle />
                </CollectionLockupContent>
              </CollectionLockup>
            ) : null}
          </ItemContent>
        </Item>
      </TableCell>
      <TableCell className={TRANSFER_FLOW_FORM_ITEM_CLASSNAMES.actions}>
        {showQuantityInput && (
          <Controller
            control={control}
            name={`items.${index}.quantity`}
            render={({ field, fieldState }) => {
              return (
                <QuantityInput
                  error={Boolean(fieldState.error?.message)}
                  max={bn(ownedQuantity).toNumber()}
                  onValueChange={value => {
                    field.onChange(value)
                  }}
                  placeholder={ownedQuantity}
                  value={field.value}
                />
              )
            }}
          />
        )}
        {/* No need to remove the item if it's the only item. */}
        {fields.length > 1 && (
          <UnstyledButton
            onClick={() => {
              remove(index)
            }}
          >
            <Close fill="interactive-primary-styles" />
          </UnstyledButton>
        )}
      </TableCell>
    </TableRow>
  )
}
